import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { io } from 'socket.io-client'
import AppConfig from '../config'
import { CLIENT_EVENT_BUS, SERVER_EVENT_BUS } from '../modules/websocket/enums'
import { eventConsumer } from '../modules/websocket/event-consumer'
import { selectAccessToken } from '../state/auth.slice'
import { selectActiveUser } from '../state/user.slice'
import {
  selectConnectionStatus,
  selectIsConnected,
  selectWebsocketId,
  setConnectionStatus,
  setIsConnected,
  setWebsocketId,
} from '../state/websocket.slice'

const SOCKET_URI = AppConfig.websocket.uri

let Socket = null

export function useWebsocket() {
  const dispatch = useDispatch()
  const isConnected = useSelector(selectIsConnected)
  const websocketId = useSelector(selectWebsocketId)
  const accessToken = useSelector(selectAccessToken)
  const status = useSelector(selectConnectionStatus)
  const activeUser = useSelector(selectActiveUser)
  const userId = activeUser?._id

  useEffect(() => {
    if (accessToken) {
      if (Socket && status === 'connected') {
        return
      }
      connect(accessToken)
    } else {
      if (Socket) {
        Socket.disconnect()
      }
    }
  }, [accessToken]) // eslint-disable-line react-hooks/exhaustive-deps

  const connect = (token) => {
    console.log('WEBSOCKET: connecting to socket', SOCKET_URI)
    if (!io) {
      console.error('socket.io is not available')
      return
    }
    if (!token) {
      console.error('socket.io cannot initialize. not token')
      return
    }
    if (status === 'connected') {
      console.info('socket.io is already connected')
      return
    }

    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      vendor: navigator.vendor,
      appVersion: navigator.appVersion,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      browserName: navigator.appName,
    }

    try {
      Socket = io(SOCKET_URI, {
        path: `/${AppConfig.websocket.namespace}`,
        query: {
          token,
          device: JSON.stringify(deviceInfo),
          namespace: AppConfig.websocket.namespace,
        },
      })
      Socket?.on('connect', onConnectionSuccess)
      Socket?.on('connect_error', onConnectionError)
      Socket?.on(SERVER_EVENT_BUS, onEvents)
    } catch (error) {
      console.log('WEBSOCKET: failed to connect to socket', error)
    }
  }

  /**
   * Emits an event to the WebSocket server.
   * @param {Object} context - The event context containing type and payload.
   * @param {string} context.type - The type of the event.
   * @param {Object} context.payload - The payload of the event.
   */
  const emit = async (context) => {
    Socket?.emit(CLIENT_EVENT_BUS, context)
  }

  /**
   * Handles events received from the WebSocket server.
   * @param {Object} context - The event context received from the server.
   */
  const onEvents = async (context) => {
    await eventConsumer(context)
  }

  const getClientId = async (context) => {
    return Socket?.id
  }

  const subscribeToChannel = useCallback(
    async (channel) => {
      if (!channel) {
        console.error('WEBSOCKET: cannot subscribe to channel. no channel')
        return
      }
      if (!userId) {
        console.error('WEBSOCKET: cannot subscribe to channel. no user id')
        return
      }
      if (!Socket) {
        console.error('WEBSOCKET: cannot subscribe to channel. no socket')
        return
      }
      Socket.emit('subscribe', {
        channel,
        userId,
      })
    },
    [userId],
  )

  const unsubscribeFromChannel = useCallback(
    async (channel) => {
      if (!channel) {
        console.error('WEBSOCKET: cannot unsubscribe from channel. no channel')
        return
      }
      if (!userId) {
        console.error('WEBSOCKET: cannot unsubscribe from channel. no user id')
        return
      }
      if (!Socket) {
        console.error('WEBSOCKET: cannot unsubscribe from channel. no socket')
        return
      }
      Socket.emit('unsubscribe', {
        channel,
        userId,
      })
    },
    [userId],
  )

  /**
   * Handles WebSocket connection errors.
   * @param {Object} error - The error object received on connection failure.
   */
  const onConnectionError = (error) => {
    console.error('WEBSOCKET: connnection error: ', error)
  }

  /**
   * Handles successful WebSocket connection.
   */
  const onConnectionSuccess = useCallback(async () => {
    if (Socket?.connected) {
      dispatch(setConnectionStatus('connected'))
      Socket.emit('reconnect_user', activeUser?._id)
      console.info('*** Connected to Northlake Websocket ***')
      if (!websocketId) {
        dispatch(setWebsocketId(Socket.id))
        dispatch(setIsConnected(true))
      } else if (websocketId && websocketId !== Socket.id) {
        dispatch(setWebsocketId(Socket.id))
        dispatch(setIsConnected(true))
      }
    }
  }, [dispatch, websocketId, activeUser])

  return {
    emit,
    getClientId,
    isConnected,
    connectionStatus: status,
    subscribeToChannel,
    unsubscribeFromChannel,
  }
}
